import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    Button,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent
} from '@mui/material';
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap } from 'react-leaflet';
import { HungerZone } from '../../../services/api';
import L from 'leaflet';

interface HungerZoneMapProps {
    hungerZones: HungerZone[];
    userLocation: [number, number] | null;
    onZoneSelect: (zone: HungerZone) => void;
}

// Set view component to update map center
interface SetViewProps {
    center: [number, number];
    zoom: number;
}

const SetView: React.FC<SetViewProps> = ({ center, zoom }) => {
    const map = useMap();
    map.setView(center, zoom);
    return null;
};

// Get color based on severity
const getSeverityColor = (severity: number): string => {
    if (severity >= 8) return '#d32f2f'; // Very high (red)
    if (severity >= 6) return '#f57c00'; // High (orange)
    if (severity >= 4) return '#fbc02d'; // Medium (yellow)
    return '#4caf50'; // Low (green)
};

// Get radius based on affected population
const getPopulationRadius = (population: number): number => {
    // Scale the radius based on population
    return Math.max(10000, Math.min(50000, population * 2));
};

const HungerZoneMap: React.FC<HungerZoneMapProps> = ({ hungerZones, userLocation, onZoneSelect }) => {
    const [viewMode, setViewMode] = useState<'severity' | 'population'>('severity');
    const [selectedZone, setSelectedZone] = useState<HungerZone | null>(null);

    // Handle view mode change
    const handleViewModeChange = (event: SelectChangeEvent) => {
        setViewMode(event.target.value as 'severity' | 'population');
    };

    // Handle zone selection
    const handleZoneSelect = (zone: HungerZone) => {
        setSelectedZone(zone);
    };

    // Get the default center location (user location or first hunger zone)
    const defaultCenter: [number, number] = userLocation ||
        (hungerZones.length > 0 ? [hungerZones[0].latitude, hungerZones[0].longitude] : [20, 0]);

    return (
        <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <FormControl fullWidth size="small">
                    <InputLabel id="view-mode-label">View Mode</InputLabel>
                    <Select
                        labelId="view-mode-label"
                        value={viewMode}
                        label="View Mode"
                        onChange={handleViewModeChange}
                    >
                        <MenuItem value="severity">Hunger Severity</MenuItem>
                        <MenuItem value="population">Affected Population</MenuItem>
                    </Select>
                </FormControl>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                        Legend:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {viewMode === 'severity' ? (
                            <>
                                <Chip size="small" sx={{ bgcolor: '#d32f2f', color: 'white' }} label="Very High (8-10)" />
                                <Chip size="small" sx={{ bgcolor: '#f57c00', color: 'white' }} label="High (6-7)" />
                                <Chip size="small" sx={{ bgcolor: '#fbc02d' }} label="Medium (4-5)" />
                                <Chip size="small" sx={{ bgcolor: '#4caf50', color: 'white' }} label="Low (1-3)" />
                            </>
                        ) : (
                            <>
                                <Chip size="small" sx={{ bgcolor: '#d32f2f', color: 'white' }} label="20k+ people" />
                                <Chip size="small" sx={{ bgcolor: '#f57c00', color: 'white' }} label="10k-20k people" />
                                <Chip size="small" sx={{ bgcolor: '#fbc02d' }} label="5k-10k people" />
                                <Chip size="small" sx={{ bgcolor: '#4caf50', color: 'white' }} label="<5k people" />
                            </>
                        )}
                    </Stack>
                </Box>
            </Stack>

            <Box sx={{ display: 'flex', height: '500px' }}>
                <Box sx={{ flex: 1, pr: { xs: 0, md: 2 }, mb: { xs: 2, md: 0 } }}>
                    <Box sx={{
                        height: '100%',
                        width: '100%',
                        '& .leaflet-container': {
                            height: '100%',
                            borderRadius: 1
                        }
                    }}>
                        <MapContainer
                            center={defaultCenter}
                            zoom={6}
                            style={{ height: '100%', width: '100%' }}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />

                            {/* Set the view to default center */}
                            <SetView center={defaultCenter} zoom={6} />

                            {/* User location marker */}
                            {userLocation && (
                                <Marker position={userLocation}>
                                    <Popup>
                                        <Typography variant="body2">Your Location</Typography>
                                    </Popup>
                                </Marker>
                            )}

                            {/* Hunger zone circles */}
                            {hungerZones.map(zone => {
                                // Determine circle properties based on view mode
                                const color = viewMode === 'severity'
                                    ? getSeverityColor(zone.severity)
                                    : zone.affectedPopulation >= 20000
                                        ? '#d32f2f'
                                        : zone.affectedPopulation >= 10000
                                            ? '#f57c00'
                                            : zone.affectedPopulation >= 5000
                                                ? '#fbc02d'
                                                : '#4caf50';

                                const radius = viewMode === 'severity'
                                    ? 30000 + (zone.severity * 5000) // Base 30km + 5km per severity point
                                    : getPopulationRadius(zone.affectedPopulation);

                                return (
                                    <Circle
                                        key={zone.id}
                                        center={[zone.latitude, zone.longitude]}
                                        radius={radius}
                                        pathOptions={{
                                            color: color,
                                            fillColor: color,
                                            fillOpacity: 0.4
                                        }}
                                        eventHandlers={{
                                            click: () => handleZoneSelect(zone)
                                        }}
                                    >
                                        <Popup>
                                            <Typography variant="subtitle2">{zone.region}</Typography>
                                            <Typography variant="body2">
                                                Hunger Severity: {zone.severity}/10
                                            </Typography>
                                            <Typography variant="body2">
                                                Affected Population: {zone.affectedPopulation.toLocaleString()}
                                            </Typography>
                                            <Box sx={{ mt: 1 }}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => onZoneSelect(zone)}
                                                >
                                                    View Details
                                                </Button>
                                            </Box>
                                        </Popup>
                                    </Circle>
                                );
                            })}
                        </MapContainer>
                    </Box>
                </Box>

                {/* Zone details panel */}
                <Box sx={{
                    width: { xs: '100%', md: '300px' },
                    display: { xs: selectedZone ? 'block' : 'none', md: 'block' }
                }}>
                    <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
                        {selectedZone ? (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    {selectedZone.region}
                                </Typography>

                                <Chip
                                    label={`Severity: ${selectedZone.severity}/10`}
                                    color={selectedZone.severity >= 8 ? "error" : selectedZone.severity >= 5 ? "warning" : "success"}
                                    sx={{ mb: 2 }}
                                />

                                <Typography variant="body2" paragraph>
                                    Affected population: {selectedZone.affectedPopulation.toLocaleString()}
                                </Typography>

                                <Typography variant="subtitle2" gutterBottom>
                                    Food Needs:
                                </Typography>
                                <Stack spacing={1} sx={{ mb: 2 }}>
                                    {selectedZone.foodNeeds.map((need, idx) => (
                                        <Typography key={idx} variant="body2">
                                            • {need.type}: {need.quantityNeeded} {need.unit}
                                        </Typography>
                                    ))}
                                </Stack>

                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={() => onZoneSelect(selectedZone)}
                                >
                                    Help This Region
                                </Button>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Select a hunger zone on the map to view details
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};

export default HungerZoneMap; 